import is from '@sindresorhus/is';
import type { InfiniteData, QueryKey, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PaginatedSkillCatalog, SkillSyncStatus } from '~/shared/data-contract/skill';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseUpdateSkillCatalogEntryParams = {
    readonly skillId: string;
    readonly syncStatus: SkillSyncStatus;
};

type UseUpdateSkillCatalogEntryContext = {
    readonly previousData: ReadonlyArray<readonly [QueryKey, InfiniteData<PaginatedSkillCatalog> | undefined]>;
};

export type UseUpdateSkillCatalogEntryResult = UseMutationResult<
    unknown,
    Error,
    UseUpdateSkillCatalogEntryParams,
    UseUpdateSkillCatalogEntryContext
>;

export const useUpdateSkillCatalogEntry = (scheduleRefetch: () => void): UseUpdateSkillCatalogEntryResult => {
    const { updateSkillCatalogEntry } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ skillId, syncStatus }: UseUpdateSkillCatalogEntryParams) => {
            const result = await updateSkillCatalogEntry({
                skillId,
                syncStatus,
            });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        onError: (_error, _variables, context) => {
            context?.previousData.forEach(([key, data]) => {
                queryClient.setQueryData(key, data);
            });
        },
        onMutate: async ({ skillId, syncStatus }) => {
            await queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.skillCatalog(),
            });

            const allCaches = queryClient.getQueriesData<InfiniteData<PaginatedSkillCatalog>>({
                queryKey: jobArchitectureKeys.skillCatalog(),
            });

            allCaches.forEach(([key]) => {
                queryClient.setQueryData<InfiniteData<PaginatedSkillCatalog>>(key, (old) => {
                    if (is.undefined(old?.pages)) {
                        return old;
                    }

                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            data: page.data.map((skill) => (skill.id === skillId ? { ...skill, syncStatus } : skill)),
                        })),
                    };
                });
            });

            return { previousData: allCaches };
        },
        onSettled: scheduleRefetch,
    });
};
